import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { EditSolidIcon } from '@dhl-ui/icons'
import { OrganisationDto, useGetBusinessUnit } from '@dhl/mdp-api'
import { Body1, Body2, Box, IconButton, Card, H6, Grid } from '@dhl-ui/components'

import { COUNTRIES } from '~/common/config/country'

import { links } from '~/AppRoutes'

type Response = {
  code: string
  name: string
}

type Params = {
  id: string
}

type Props = {
  values?: OrganisationDto
}

export const TerminalDetails = ({ values }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<Params>()

  const { data: allBusinessUnits } = useGetBusinessUnit()

  if (!allBusinessUnits) return null

  const filteredBusinessUnit = allBusinessUnits?.filter((businessUnit) => {
    return businessUnit?.code === values?.businessUnitCode
  })

  const filteredCountry = COUNTRIES.filter(({ value }) => {
    return value === values?.data?.address?.countryCode
  })

  return (
    <Card>
      <Card.Header
        title={t('admin.terminalsPage.terminalDetailsPage.titleText')}
        endNode={
          <IconButton
            aria-label="edit terminal"
            data-testid="edit-terminal-details"
            onClick={() => id && navigate(links.editTerminalLink(id))}
          >
            <EditSolidIcon />
          </IconButton>
        }
      />
      <Card.Content>
        <Grid container direction="column" justifyContent="center" alignItems="stretch">
          <Grid>
            <Box>
              <H6>{t('admin.terminalsPage.terminalDetailsPage.accountInformation')}</H6>
            </Box>
          </Grid>
          <Grid container direction="row" justifyContent="center" alignItems="stretch">
            <Grid size={{ xs: 6 }}>
              <Box marginTop={2}>
                <Body2 variant="tertiary">{t('admin.terminalsPage.terminalDetailsPage.name')}</Body2>
                <Body1>{values?.name}</Body1>
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box marginTop={2}>
                <Body2 variant="tertiary">{t('admin.terminalsPage.terminalDetailsPage.businessUnit')}</Body2>
                <Body1>{filteredBusinessUnit && filteredBusinessUnit[0]?.name}</Body1>
              </Box>
            </Grid>
          </Grid>

          <Grid>
            <Box marginTop={2}>
              <H6>{t('admin.terminalsPage.terminalDetailsPage.addressInformation')}</H6>
            </Box>
          </Grid>
          <Grid container direction="row" justifyContent="center" alignItems="stretch">
            <Grid size={{ xs: 4 }}>
              <Box marginTop={2}>
                <Body2 variant="tertiary">{t('admin.terminalsPage.terminalDetailsPage.postalCode')}</Body2>
                <Body1>{values?.data?.address?.zipCode}</Body1>
              </Box>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Box marginTop={2}>
                <Body2 variant="tertiary">{t('admin.terminalsPage.terminalDetailsPage.number')}</Body2>
                <Body1>{values?.data?.address?.number}</Body1>
              </Box>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Box marginTop={2}>
                <Body2 variant="tertiary">{t('admin.terminalsPage.terminalDetailsPage.addition')}</Body2>
                <Body1>{values?.data?.address?.addition}</Body1>
              </Box>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Box marginTop={2}>
                <Body2 variant="tertiary">{t('admin.terminalsPage.terminalDetailsPage.street')}</Body2>
                <Body1>{values?.data?.address?.street}</Body1>
              </Box>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Box marginTop={2}>
                <Body2 variant="tertiary">{t('admin.terminalsPage.terminalDetailsPage.city')}</Body2>
                <Body1>{values?.data?.address?.city}</Body1>
              </Box>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Box marginTop={2}>
                <Body2 variant="tertiary">{t('admin.terminalsPage.terminalDetailsPage.country')}</Body2>
                <Body1>{filteredCountry[0]?.label}</Body1>
              </Box>
            </Grid>
          </Grid>

          <Grid>
            <Box marginTop={2}>
              <H6>{t('admin.terminalsPage.terminalDetailsPage.contact')}</H6>
            </Box>
          </Grid>
          <Grid container direction="row" justifyContent="center" alignItems="stretch">
            <Grid size={{ xs: 6 }}>
              <Box marginTop={2}>
                <Body2 variant="tertiary">{t('admin.terminalsPage.terminalDetailsPage.name')}</Body2>
                <Body1>{values?.data?.contactPerson?.firstName}</Body1>
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box marginTop={2}>
                <Body2 variant="tertiary">{t('admin.terminalsPage.terminalDetailsPage.surname')}</Body2>
                <Body1>{values?.data?.contactPerson?.lastName}</Body1>
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box marginTop={2}>
                <Body2 variant="tertiary">{t('admin.terminalsPage.terminalDetailsPage.email')}</Body2>
                <Body1>{values?.data?.contactPerson?.email.address}</Body1>
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box marginTop={2}>
                <Body2 variant="tertiary">{t('admin.terminalsPage.terminalDetailsPage.phoneNumber')}</Body2>
                <Body1>{values?.data?.contactPerson?.phone}</Body1>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Card.Content>
    </Card>
  )
}
